import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  TextField, Switch, Stack, Chip, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import useConfirm from '../calendar/dialogs/UseConfirm';
import { hasAnyRequiredPermission, WORKSHIFT_GLOBAL_WRITE_PERMISSIONS } from '../../config/routePermissions';
import { useSnackbar } from 'notistack';

export default function WorkshiftGlobalConfigPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, ConfirmDialog } = useConfirm();
  const { permissions } = useContext(UserContext);
  const canRead = permissions.includes('workshifts.read');
  const canWrite = hasAnyRequiredPermission(permissions, WORKSHIFT_GLOBAL_WRITE_PERMISSIONS);
  const [loading, setLoading] = useState(false);
  const [allWorkshifts, setAllWorkshifts] = useState([]);
  const [showDisabled, setShowDisabled] = useState(true);

  // modales
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // form (alta/edición)
  const [name, setName] = useState('');
  const [hourFrom, setHourFrom] = useState('08:00');
  const [hourTo, setHourTo] = useState('16:00');
  const [isNight, setIsNight] = useState(false);
  const [description, setDescription] = useState('');
  const [hasBreak, setHasBreak] = useState(false);
  const [breakFrom, setBreakFrom] = useState('12:00');
  const [breakTo, setBreakTo] = useState('15:00');

  // errores
  const [editError, setEditError] = useState('');
  const [addError, setAddError] = useState('');

  const fetchWorkshifts = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/workshift');
      setAllWorkshifts(res.data || []);
    } catch {
      enqueueSnackbar('Error al listar turnos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) {
      fetchWorkshifts();
    }
  }, [canRead]);

  const workshifts = useMemo(() => {
    const onlyGlobal = (allWorkshifts || []).filter(w => w.is_global === true);
    const filtered = showDisabled ? onlyGlobal : onlyGlobal.filter(w => w.enabled);
    return [...filtered].sort((a, b) => {
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
      return (a.hour_from || '').localeCompare(b.hour_from || '');
    });
  }, [allWorkshifts, showDisabled]);

  // ---------- Validación: turno global duplicado ----------
  const isDuplicateGlobalName = (name, excludeId = null) => {
    const normalizedExcludeId =
      excludeId === null || excludeId === undefined ? null : String(excludeId);

    return allWorkshifts.some(ws => 
      ws.is_global === true &&
      ws.name.trim().toLowerCase() === name.trim().toLowerCase() &&
      (normalizedExcludeId === null || String(ws.id) !== normalizedExcludeId)
    );
  };

  // ---------- Turno cortado: helpers ----------
  const fmtRange = (ws) => {
    const from = (ws.hour_from || '').slice(0, 5);
    const to = (ws.hour_to || '').slice(0, 5);
    if (ws.break_from && ws.break_to) {
      const bFrom = ws.break_from.slice(0, 5);
      const bTo = ws.break_to.slice(0, 5);
      return `${from}–${bFrom} / ${bTo}–${to}`;
    }
    return `${from}–${to}`;
  };

  const validateBreak = () => {
    if (!hasBreak) return null;
    if (!breakFrom || !breakTo) return 'Debe indicar el horario de descanso completo';
    if (!(hourFrom < breakFrom && breakFrom < breakTo && breakTo < hourTo)) {
      return 'El descanso debe estar dentro del horario del turno (Desde < Descanso desde < Descanso hasta < Hasta)';
    }
    return null;
  };

  // ---- edición ----
  const openEdit = (ws) => {
    setEditing(ws);
    setName(ws.name || '');
    setHourFrom((ws.hour_from || '08:00').slice(0, 5));
    setHourTo((ws.hour_to || '16:00').slice(0, 5));
    setIsNight(!!ws.is_night);
    setDescription(ws.description || '');
    setHasBreak(Boolean(ws.break_from && ws.break_to));
    setBreakFrom((ws.break_from || '12:00').slice(0, 5));
    setBreakTo((ws.break_to || '15:00').slice(0, 5));
    setEditError('');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditError('');

    if (!editing) return;

    const normalizedName = name.trim().toLowerCase();
    const originalName = String(editing.name || '').trim().toLowerCase();

    if (!name.trim()) {
      setEditError('El nombre del turno es obligatorio');
      return;
    }

    // Validar duplicado solo si cambió el nombre
    if (normalizedName !== originalName && isDuplicateGlobalName(name, editing.id)) {
      setEditError(`Ya existe otro turno global llamado "${name.trim()}"`);
      return;
    }

    const breakError = validateBreak();
    if (breakError) {
      setEditError(breakError);
      return;
    }

    try {
      const params = new URLSearchParams({
        name: name.trim(),
        hour_from: hourFrom,
        hour_to: hourTo,
        is_night: String(isNight),
        enabled: String(editing.enabled),
        description: description.trim(),
        is_global: 'true',
      });
      if (hasBreak) {
        params.set('break_from', breakFrom);
        params.set('break_to', breakTo);
      } else {
        params.set('break_from', '');
        params.set('break_to', '');
      }
      await axios.patch(`/workshift/${editing.id}?${params.toString()}`);
      setEditOpen(false);
      setEditing(null);
      fetchWorkshifts();
    } catch (error) {
      setEditError(error.response?.data?.error || error.response?.data?.message || 'Error al guardar cambios del turno');
    }
  };

  // ---- alta ----
  const openAdd = () => {
    setName('');
    setHourFrom('08:00');
    setHourTo('16:00');
    setIsNight(false);
    setDescription('');
    setHasBreak(false);
    setBreakFrom('12:00');
    setBreakTo('15:00');
    setAddError('');
    setAddOpen(true);
  };

  const saveAdd = async () => {
    setAddError('');

    if (!name.trim()) {
      setAddError('El nombre del turno es obligatorio');
      return;
    }

    // Validar duplicado
    if (isDuplicateGlobalName(name)) {
      setAddError(`Ya existe un turno global llamado "${name.trim()}"`);
      return;
    }

    const breakError = validateBreak();
    if (breakError) {
      setAddError(breakError);
      return;
    }

    try {
      const params = new URLSearchParams({
        name: name.trim(),
        hour_from: hourFrom,
        hour_to: hourTo,
        is_night: String(isNight),
        enabled: 'true',
        description: description.trim(),
        is_global: 'true',
      });
      if (hasBreak) {
        params.set('break_from', breakFrom);
        params.set('break_to', breakTo);
      }
      await axios.post(`/workshift?${params.toString()}`);
      setAddOpen(false);
      fetchWorkshifts();
    } catch (error) {
      setAddError(error.response?.data?.error || error.response?.data?.message || 'Error al crear el turno');
    }
  };

  // ---- habilitar / deshabilitar ----
  const disableWorkshift = async (ws) => {
    const ok = await confirm({
      title: 'Deshabilitar turno global',
      content: `¿Seguro que querés deshabilitar el turno global "${ws.name}"?`,
      confirmText: 'Deshabilitar',
    });
    if (!ok) return;
    try {
      await axios.delete(`/workshift/${ws.id}`);
      fetchWorkshifts();
    } catch {
      enqueueSnackbar('Error al deshabilitar el turno', { variant: 'error' });
    }
  };

  const enableWorkshift = async (ws) => {
    try {
      const params = new URLSearchParams({ enabled: 'true' });
      await axios.patch(`/workshift/${ws.id}?${params.toString()}`);
      fetchWorkshifts();
    } catch {
      enqueueSnackbar('Error al habilitar el turno', { variant: 'error' });
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="error">No tienes permiso para ver turnos globales.</Typography>
      </Box>
    );
  }

  return (
    <>
      {ConfirmDialog}
      {/* Header */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="h4" align="center">Turnos Globales</Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
          Turnos reutilizables para todas las estaciones.
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5, mb: 1.5, px: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">Mostrar deshabilitados</Typography>
            <Switch checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
          </Stack>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              Agregar turno global
            </Button>
          )}
        </Stack>
      </Box>

      {/* Tabla */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 1000 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Horario</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && workshifts.map(ws => (
                <TableRow key={ws.id} sx={!ws.enabled ? { backgroundColor: '#f7f7f7' } : {}}>
                  <TableCell sx={!ws.enabled ? { color: '#999', textDecoration: 'line-through' } : {}}>
                    {ws.name}
                  </TableCell>
                  <TableCell sx={!ws.enabled ? { color: '#999' } : {}}>
                    {fmtRange(ws)}
                  </TableCell>
                  <TableCell>
                    {ws.is_night ? <Chip size="small" label="Nocturno" /> : <Chip size="small" label="Diurno" variant="outlined" />}
                  </TableCell>
                  <TableCell sx={!ws.enabled ? { color: '#999' } : {}}>
                    {ws.description || '—'}
                  </TableCell>
                  <TableCell>
                    {ws.enabled
                      ? <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          <CheckCircleIcon fontSize="small" /> Habilitado
                        </Stack>
                      : <span style={{ color: '#999', fontWeight: 600 }}>Deshabilitado</span>
                    }
                  </TableCell>
                  <TableCell align="right">
                    {canWrite && (
                      <>
                        <IconButton size="small" onClick={() => openEdit(ws)} title="Editar">
                          <EditIcon />
                        </IconButton>
                        {ws.enabled ? (
                          <IconButton size="small" onClick={() => disableWorkshift(ws)} title="Deshabilitar">
                            <BlockIcon />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => enableWorkshift(ws)} title="Habilitar">
                            <RestoreIcon />
                          </IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {loading && (
                <TableRow><TableCell colSpan={6}>Cargando…</TableCell></TableRow>
              )}
              {!loading && workshifts.length === 0 && (
                <TableRow><TableCell colSpan={6}>No hay turnos globales para mostrar.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Modal editar */}
      <Dialog open={editOpen && canWrite} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar turno global</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError('')}>
                {editError}
              </Alert>
            )}

            <TextField label="Nombre" value={name} onChange={e => setName(e.target.value)} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Desde" type="time" value={hourFrom} onChange={e => setHourFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              <TextField label="Hasta" type="time" value={hourTo} onChange={e => setHourTo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Nocturno?</Typography>
              <Switch checked={isNight} onChange={e => setIsNight(e.target.checked)} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Turno cortado (con descanso)?</Typography>
              <Switch checked={hasBreak} onChange={e => setHasBreak(e.target.checked)} />
            </Stack>
            {hasBreak && (
              <Stack direction="row" spacing={2}>
                <TextField label="Descanso desde" type="time" value={breakFrom} onChange={e => setBreakFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
                <TextField label="Descanso hasta" type="time" value={breakTo} onChange={e => setBreakTo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              </Stack>
            )}
            <TextField label="Descripción" value={description} onChange={e => setDescription(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveEdit}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal agregar */}
      <Dialog open={addOpen && canWrite} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Agregar turno global</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {addError && (
              <Alert severity="error" onClose={() => setAddError('')}>
                {addError}
              </Alert>
            )}

            <TextField label="Nombre" value={name} onChange={e => setName(e.target.value)} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Desde" type="time" value={hourFrom} onChange={e => setHourFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              <TextField label="Hasta" type="time" value={hourTo} onChange={e => setHourTo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Nocturno?</Typography>
              <Switch checked={isNight} onChange={e => setIsNight(e.target.checked)} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography>¿Turno cortado (con descanso)?</Typography>
              <Switch checked={hasBreak} onChange={e => setHasBreak(e.target.checked)} />
            </Stack>
            {hasBreak && (
              <Stack direction="row" spacing={2}>
                <TextField label="Descanso desde" type="time" value={breakFrom} onChange={e => setBreakFrom(e.target.value)} inputProps={{ step: 300 }} fullWidth />
                <TextField label="Descanso hasta" type="time" value={breakTo} onChange={e => setBreakTo(e.target.value)} inputProps={{ step: 300 }} fullWidth />
              </Stack>
            )}
            <TextField label="Descripción" value={description} onChange={e => setDescription(e.target.value)} fullWidth />
            <Typography variant="caption" color="text.secondary">
              * Estos turnos quedan disponibles para todas las estaciones.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveAdd}>Crear</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
